/**
 * DocumentEditModal — edit notes / tags / effective_year for one document.
 *
 * Rename is inline on the card (see DocumentCard.jsx) — this modal is
 * for the fields that need more space. Rendered as a portal-free
 * fixed-positioned overlay to keep the dependency footprint minimal.
 *
 * Save button PATCHes `/api/documents/{id}` with the diffed payload,
 * then calls `onSaved(updatedDoc)` so the parent list can update in
 * place without a full refetch.
 */
import { useEffect, useState } from "react";
import { Loader2, Save, X } from "lucide-react";
import { api, formatApiError } from "@/api/client";

const NOTES_MAX_LENGTH = 500;

export default function DocumentEditModal({ doc, open, onClose, onSaved }) {
  const [notes, setNotes] = useState(doc?.notes || "");
  const [tags, setTags] = useState((doc?.tags || []).join(", "));
  const [year, setYear] = useState(doc?.effective_year ? String(doc.effective_year) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && doc) {
      setNotes(doc.notes || "");
      setTags((doc.tags || []).join(", "));
      setYear(doc.effective_year ? String(doc.effective_year) : "");
      setError(null);
    }
  }, [open, doc]);

  if (!open || !doc) return null;

  const payload = {};
  const currNotes = doc.notes || "";
  const currTags = (doc.tags || []).join(", ");
  const currYear = doc.effective_year ? String(doc.effective_year) : "";
  if (notes !== currNotes) payload.notes = notes;
  if (tags.trim() !== currTags.trim()) {
    payload.tags = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  if (year !== currYear) {
    payload.effective_year = year && /^\d{4}$/.test(year) ? parseInt(year, 10) : null;
  }
  const nothingToSave = Object.keys(payload).length === 0;

  async function handleSave() {
    if (nothingToSave) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch(`/documents/${doc.id}`, payload);
      onSaved && onSaved(res.data);
      onClose();
    } catch (err) {
      setError(formatApiError(err) || "Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={`documents-edit-modal-backdrop-${doc.id}`}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(10,10,15,0.72)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <section
        data-testid={`documents-edit-modal-${doc.id}`}
        className="w-full max-w-md rounded-3xl p-6"
        style={{
          background: "var(--v2-surface)",
          border: "1px solid var(--v2-border)",
        }}
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-v2-textMuted">
              Edit details
            </p>
            <h3 className="mt-1 text-lg font-semibold text-v2-text line-clamp-1">
              {doc.display_name}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid={`documents-edit-modal-close-${doc.id}`}
            className="p-1 text-v2-textMuted hover:text-v2-text transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-v2-textMuted">
              Notes
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX_LENGTH))}
              rows={4}
              maxLength={NOTES_MAX_LENGTH}
              disabled={busy}
              data-testid={`documents-edit-modal-notes-${doc.id}`}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:border-v2-accent"
              style={{
                background: "var(--v2-surface-subtle)",
                border: "1px solid var(--v2-border)",
              }}
            />
            <p className="mt-1 text-[10px] text-v2-textMuted text-right">
              {notes.length} / {NOTES_MAX_LENGTH}
            </p>
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-v2-textMuted">
              Tags (comma-separated)
            </span>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              disabled={busy}
              data-testid={`documents-edit-modal-tags-${doc.id}`}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:border-v2-accent"
              style={{
                background: "var(--v2-surface-subtle)",
                border: "1px solid var(--v2-border)",
              }}
            />
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-v2-textMuted">
              Effective year (optional)
            </span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              value={year}
              onChange={(e) => setYear(e.target.value.replace(/\D/g, ""))}
              disabled={busy}
              data-testid={`documents-edit-modal-year-${doc.id}`}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:border-v2-accent"
              style={{
                background: "var(--v2-surface-subtle)",
                border: "1px solid var(--v2-border)",
              }}
            />
          </label>

          {error && (
            <p
              data-testid={`documents-edit-modal-error-${doc.id}`}
              className="text-[12px] text-red-400"
            >
              {error}
            </p>
          )}
        </div>

        <footer className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid={`documents-edit-modal-cancel-${doc.id}`}
            className="rounded-full px-4 py-2 text-xs text-v2-textMuted hover:text-v2-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || nothingToSave}
            data-testid={`documents-edit-modal-save-${doc.id}`}
            className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold text-v2-background bg-v2-accent hover:bg-white disabled:opacity-60 transition-colors"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
        </footer>
      </section>
    </div>
  );
}
